import AARTextField from "@/components/AARTextField";
import BaseDrawer from "@/components/BaseDrawer";
import CustomLabel from "@/components/CustomLabel";
import { useAllLevels } from "@/hooks/useLevels";
import { useAllUsers } from "@/hooks/useUsers";
import { useAppSelector } from "@/store/hooks/hooks";
import { RootState } from "@/store/store";
import { buildFilters } from "@/utils/kendo";
import { MultiSelect, MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import {
  CheckboxChangeEvent,
  TextAreaChangeEvent,
  TextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { useEffect, useState } from "react";

interface IProps {
  showCreateDrawer: boolean;
  setShowCreateDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  addNewUserGroup: any;
  handleChange: (
    e:
      | TextBoxChangeEvent
      | TextAreaChangeEvent
      | CheckboxChangeEvent
      | MultiSelectChangeEvent
  ) => void;
  onSave: () => void;
}

function WriteDrawer({
  showCreateDrawer,
  setShowCreateDrawer,
  addNewUserGroup,
  handleChange,
  onSave,
  ...drawerProps
}: IProps) {
  const { selectedProject } = useAppSelector(
    (state: RootState) => state.projects
  );

  const [invalidFields, setInvalidFields] = useState<{
    [key: string]: boolean;
  }>({});

  const { data: levelsData } = useAllLevels(
    {
      filter: buildFilters(selectedProject?.id, undefined, false, [
        { field: "status", operator: "eq", value: "Published" },
      ]),
    },
    { enabled: true }
  );

  // Build custom filter for subcontractors only
  const subcontractorFilter = {
    logic: "and" as const,
    filters: [
      {
        logic: "or" as const,
        filters: [
          {
            field: "Projects",
            operator: "array-contains",
            value: selectedProject?.id,
          },
          {
            field: "Roles",
            operator: "array-contains",
            value: "Super Admin",
          },
        ],
      },
      {
        logic: "and" as const,
        filters: [
          {
            field: "isActive",
            operator: "eq",
            value: true,
          },
        ],
      },
      {
        logic: "and" as const,
        filters: [
          {
            field: "Roles",
            operator: "array-contains",
            value: "Sub Contractor",
          },
        ],
      },
    ],
  };

  const { data: usersData } = useAllUsers(
    {
      filter: subcontractorFilter,
    },
    { enabled: true }
  );

  // Build custom filter for engineers only
  const engineerFilter = {
    logic: "and" as const,
    filters: [
      {
        logic: "or" as const,
        filters: [
          {
            field: "Projects",
            operator: "array-contains",
            value: selectedProject?.id,
          },
          {
            field: "Roles",
            operator: "array-contains",
            value: "Super Admin",
          },
        ],
      },
      {
        logic: "and" as const,
        filters: [
          {
            field: "isActive",
            operator: "eq",
            value: true,
          },
        ],
      },
      {
        logic: "and" as const,
        filters: [
          {
            field: "Roles",
            operator: "array-contains",
            value: "Engineer",
          },
        ],
      },
    ],
  };

  const { data: engineersData } = useAllUsers(
    {
      filter: engineerFilter,
    },
    { enabled: true }
  );

  const [filteredLevels, setFilteredLevels] = useState(levelsData?.data || []);
  const [levelFilterText, setLevelFilterText] = useState("");

  const [filteredUsers, setFilteredUsers] = useState(usersData?.data || []);
  const [userFilterText, setUserFilterText] = useState("");

  const [filteredEngineers, setFilteredEngineers] = useState(engineersData?.data || []);
  const [engineerFilterText, setEngineerFilterText] = useState("");

  const handleLevelFilterChange = (event: any) => {
    const filterValue = event.filter?.value?.toLowerCase() || "";
    setLevelFilterText(filterValue);

    if (!filterValue) {
      const sorted = [...(levelsData?.data || [])].sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setFilteredLevels(sorted);
    } else {
      const filtered = (levelsData?.data || [])
        .filter((level: any) => level.name.toLowerCase().includes(filterValue))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFilteredLevels(filtered);
    }
  };

  const handleUserFilterChange = (event: any) => {
    const filterValue = event.filter?.value?.toLowerCase() || "";
    setUserFilterText(filterValue);

    if (!filterValue) {
      const sorted = [...(usersData?.data || [])].sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setFilteredUsers(sorted);
    } else {
      const filtered = (usersData?.data || [])
        .filter((user: any) => user.name.toLowerCase().includes(filterValue))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFilteredUsers(filtered);
    }
  };

  const handleEngineerFilterChange = (event: any) => {
    const filterValue = event.filter?.value?.toLowerCase() || "";
    setEngineerFilterText(filterValue);

    if (!filterValue) {
      const sorted = [...(engineersData?.data || [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setFilteredEngineers(sorted);
    } else {
      const filtered = (engineersData?.data || [])
        .filter((engineer: any) => engineer.name.toLowerCase().includes(filterValue))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFilteredEngineers(filtered);
    }
  };

  useEffect(() => {
    if (levelsData?.data) {
      const sorted = [...levelsData.data].sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setFilteredLevels(sorted);
    }
  }, [levelsData]);

  useEffect(() => {
    if (usersData?.data) {
      const sorted = [...usersData.data].sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setFilteredUsers(sorted);
    }
  }, [usersData]);

  useEffect(() => {
    if (engineersData?.data) {
      const sorted = [...engineersData.data].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setFilteredEngineers(sorted);
    }
  }, [engineersData]);

  const handleValidation = (name: string, isValid: boolean) => {
    setInvalidFields((prev: any) => ({
      ...prev,
      [name]: !isValid,
    }));
  };

  const validateSave = () => {
    return (
      Object.values(invalidFields).every((field) => !field) &&
      addNewUserGroup?.name &&
      addNewUserGroup?.levels?.length > 0 &&
      addNewUserGroup?.users?.length > 0
    
    );
  };

  useEffect(() => {
    if (!showCreateDrawer) {
      setInvalidFields({});
    }
  }, [showCreateDrawer]);

  const getLevelsValue = () => {
    if (!addNewUserGroup?.levels || addNewUserGroup.levels.length === 0) {
      return [];
    }
    const selectedValues = levelsData?.data?.filter((item: any) =>
      addNewUserGroup?.levels?.includes(item.id)
    );
    return selectedValues || [];
  };

  const getUsersValue = () => {
    if (!addNewUserGroup?.users || addNewUserGroup.users.length === 0) {
      return [];
    }
    const selectedValues = usersData?.data?.filter((item: any) =>
      addNewUserGroup?.users?.includes(item.id)
    );
    return selectedValues || [];
  };

  const getEngineersValue = () => {
    if (!addNewUserGroup?.engineers || addNewUserGroup.engineers.length === 0) {
      return [];
    }
    const selectedValues = engineersData?.data?.filter((item: any) =>
      addNewUserGroup?.engineers?.includes(item.id)
    );
    return selectedValues || [];
  };

  const renderDrawerContent = () => (
    <div className="grid gap-4">
      <div>
        <AARTextField
          name="name"
          value={addNewUserGroup?.name}
          onChange={handleChange}
          onValidate={(name, isValid) =>
            setInvalidFields((prev) => ({
              ...prev,
              [name]: !isValid,
            }))
          }
          label="User Group Name"
          required={true}
          placeholder="Enter User Group Name"
          disabled={false}
        />
      </div>
      <div>
        <CustomLabel label="Levels" required={true} />
        <MultiSelect
          data={filteredLevels}
          textField="name"
          dataItemKey="id"
          name="levels"
          value={getLevelsValue()}
          onChange={handleChange}
          onFilterChange={handleLevelFilterChange}
          placeholder="Select Levels"
          filterable={true}
          disabled={false}
        />
      </div>
      <div className="users-multiselect">
        <CustomLabel label="Subcontractors" required={true} />
        <MultiSelect
          data={filteredUsers}
          textField="name"
          dataItemKey="id"
          name="users"
          value={getUsersValue()}
          onChange={handleChange}
          onFilterChange={handleUserFilterChange}
          placeholder="Select Subcontractors"
          filterable={true}
          disabled={false}
        />
      </div>
      <div className="users-multiselect">
        <CustomLabel label="Engineers" required={false} />
        <MultiSelect
          data={filteredEngineers}
          textField="name"
          dataItemKey="id"
          name="engineers"
          value={getEngineersValue()}
          onChange={handleChange}
          onFilterChange={handleEngineerFilterChange}
          placeholder="Select Engineers"
          filterable={true}
          disabled={false}
        />
      </div>
    </div>
  );

  return (
    <>
      <BaseDrawer
        isOpen={showCreateDrawer}
        onClose={() => setShowCreateDrawer(false)}
        title={addNewUserGroup?.id ? "Edit User Group" : "Add New User Group"}
        primaryActionText="Save User Group"
        onPrimaryAction={onSave}
        primaryActionDisabled={!validateSave()}
        {...drawerProps}
        maxW="35vw"
      >
        {renderDrawerContent()}
      </BaseDrawer>
    </>
  );
}

export default WriteDrawer;
